// @ts-nocheck
import React, { useEffect, useMemo } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useAuth } from '@/auth/clerkShim';
import _ from 'lodash';

import useOrganizationStore from '@/store/organizationStore';
import useEmailTemplateStore from '@/store/emailTemplateStore';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { Mail, ChevronRight } from 'lucide-react';

const TEMPLATES: { key: string; title: string; description: string }[] = [
  {
    key: 'team_registration_confirmation',
    title: 'Team Registration Confirmation',
    description: 'Sent to the team manager after they submit players to register their team.',
  },
  {
    key: 'payment_confirmation',
    title: 'Payment Confirmation',
    description: 'Sent to the payer after a successful Stripe checkout.',
  },
];

export default function EmailTemplateAdmin() {
  const { getToken } = useAuth();
  const { leagueName = '' } = useOutletContext() as { leagueName?: string };
  const { organizations } = useOrganizationStore();

  const { templates, getTemplatesByOrganization: getTemplatesByOrgFn } = useEmailTemplateStore((state: any) => ({
    templates: state.templates,
    getTemplatesByOrganization: state.getTemplatesByOrganization,
  }));
  const getTemplatesByOrganization = useMemo(
    () => getTemplatesByOrgFn(getToken),
    [getTemplatesByOrgFn, getToken],
  );

  const organization = organizations?.find((l: any) => l.name === leagueName);

  useEffect(() => {
    if (organization?._id) getTemplatesByOrganization(organization._id);
  }, [organization?._id]);

  const lookupEnabled = (key: string) => {
    const found = templates.find((t: any) => t.templateKey === key);
    if (!found) return null;
    return !!found.enabled && (found.subject || found.body);
  };

  return (
    // fullWidth: rail-mode page (2026-09-11) — use the width like Appearance.
    <PageContainer fullWidth>
      <PageHeader
        title={(
          <span className="flex items-center space-x-2">
            <Mail className="w-5 h-5 text-primary" />
            <span>Email Templates</span>
          </span>
        )}
        description="Customize the emails your members receive. Custom templates are sent in addition to the built-in confirmations. They don't replace them."
      />

      <div className="grid grid-cols-1 gap-3">
        {TEMPLATES.map((t) => {
          const enabled = lookupEnabled(t.key);
          const status = enabled === null ? 'Not configured' : enabled ? 'Active' : 'Disabled';
          const statusVariant = enabled === true ? 'default' : 'secondary';
          return (
            <Link key={t.key} to={`/admin/email-templates/${t.key}`} className="no-underline">
              <Card className="hover:shadow-md transition-shadow cursor-pointer">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <CardTitle className="text-base">{t.title}</CardTitle>
                      <CardDescription>{t.description}</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={statusVariant as any}>{status}</Badge>
                    <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
                  </div>
                </CardHeader>
              </Card>
            </Link>
          );
        })}
      </div>
    </PageContainer>
  );
}
